import { getAnthropicApiKey } from "../lockbox/classification/claudeClassifier.js";

/**
 * @typedef {{ role: 'user'|'assistant', text: string }} HelpChatMessage
 */

/**
 * @param {HelpChatMessage[]} history
 * @param {{ habitName?: string, currentStreak?: number }} context
 * @returns {Promise<string>}
 */
export async function getAppHelpReply(history, context = {}) {
  const apiKey = getAnthropicApiKey();
  const lastUser = [...history].reverse().find((m) => m.role === "user")?.text || "";
  const isFirstTurn = history.filter((m) => m.role === "user").length <= 1;

  if (!apiKey) {
    return scriptedAppHelpReply(lastUser, context, isFirstTurn);
  }

  const system = `You are Procsystim Help — a warm, practical assistant inside a habit and focus app.
Ask what the user needs help with if they have not said yet. Then guide them clearly.

App areas you can help with:
- Habits: custom habits on Home, marking success/fail, streaks and Insights chart
- Urges: "I feel an urge" timer on Home
- Articles & recommended books in the menu
- Community: pseudonym user ID, sharing wins and encouraging others
- Screen Time passcode: locks Screen Time, Parental Filter, App Library, Lockbox Lab; recovery email or Help session with habit stats
- Parental Filter (DNS VPN on Android), Lockbox Lab (app blocking), App Library
- Environment design, triggers, relapse patterns

Never shame the user. Keep replies under 100 words unless they ask for detail.
User habit: "${context.habitName || "Procsystim"}", current streak: ${context.currentStreak ?? 0} days.`;

  const messages = history.map((m) => ({
    role: m.role === "user" ? "user" : "assistant",
    content: m.text,
  }));

  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        model: "claude-3-5-haiku-20241022",
        max_tokens: 400,
        temperature: 0.35,
        system,
        messages,
      }),
    });
    if (!res.ok) throw new Error("api");
    const data = await res.json();
    const text = data?.content?.find((c) => c.type === "text")?.text?.trim();
    return text || scriptedAppHelpReply(lastUser, context, isFirstTurn);
  } catch {
    return scriptedAppHelpReply(lastUser, context, isFirstTurn);
  }
}

/**
 * @param {string} userText
 * @param {{ habitName?: string, currentStreak?: number }} context
 * @param {boolean} isFirstTurn
 */
function scriptedAppHelpReply(userText, context, isFirstTurn) {
  const t = userText.toLowerCase();

  if (!userText.trim() || isFirstTurn) {
    return "What do you need help with today? For example: habits, urges, passcode lock, blocking apps, community, or reading recommendations.";
  }
  if (t.includes("passcode") || t.includes("password") || t.includes("lock") || t.includes("unlock")) {
    return "Screen Time passcode locks Screen Time, Parental Filter, App Library, and Lockbox Lab. On the lock screen tap Forgot passcode → Help session. You can recover with email + recovery password, or Verify & reveal using your habit name and best streak from Insights.";
  }
  if (t.includes("community") || t.includes("user id") || t.includes("userid")) {
    return "Community uses a nickname (User ID), not your real name. Pick something unique, then share habits, books, systems, or short encouragement posts. You can reply to others with encouraging comments.";
  }
  if (t.includes("notification") || t.includes("message")) {
    return "Daily check-in notifications (8:00 and 20:00) include Mark today as success and Mark fail on the lock screen. Turn them off in Screen Time → Daily check-in notifications. Open Notifications from the Home menu for follow-ups after a fail.";
  }
  if (t.includes("habit") || t.includes("streak")) {
    return `On Home, use PROGRESS to add habits and track counts. Mark success daily on the calendar. Insights shows your streak (${context.currentStreak ?? 0} days now) and history. A fail resets the current streak but your data stays for learning.`;
  }
  if (t.includes("urge")) {
    return "Tap I feel an urge on Home for a timed breathing window. Urges peak and fade — riding them out is practice, not failure.";
  }
  if (t.includes("block") || t.includes("filter") || t.includes("dns") || t.includes("lockbox")) {
    return "Parental Filter uses DNS blocking (Android VPN). Lockbox Lab blocks apps by category. App Library classifies installed apps. Screen Time passcode protects all of these from quick changes.";
  }
  if (t.includes("book") || t.includes("article") || t.includes("read")) {
    return "Open Recommended books or Articles from the Home menu. You can share what helped you in Community under Article or book.";
  }
  if (t.includes("environment") || t.includes("trigger")) {
    return "When you mark a fail, save triggers you reuse. Redesign your space: charge phone away from bed, remove apps from home screen, use Work mode suggestions in Lockbox.";
  }
  return "Tell me more — habits, urges, blocking, passcode, community, or something else in Procsystim? I'll point you to the right screen.";
}
